import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LineChart as LineChartIcon, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import {
  buildScoringInputFromContext,
  getRawContext,
} from "@/lib/scoring-engine";
import { useMarketCalibration, metroFromContext } from "@/lib/market-calibration";
import { SensitivityOverlay } from "@/components/SensitivityOverlay";
import { HelpTooltip } from "@/components/HelpTooltip";

const Sensitivity = () => {
  const navigate = useNavigate();
  const ctx = useMemo(() => getRawContext(), []);
  const calibration = useMarketCalibration(metroFromContext(ctx));
  const scoringInput = useMemo(
    () => ({ ...buildScoringInputFromContext(), market_calibration: calibration }),
    [calibration],
  );

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) { navigate("/auth"); return; }
    });
    if (!ctx.unit_count && !ctx.purchase_price) {
      navigate("/context");
    }
  }, [navigate, ctx]);

  return (
    <div className="flex flex-col">
      <div className="w-full space-y-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-2 mb-2">
            <LineChartIcon className="w-5 h-5 text-accent" />
            <span className="font-mono text-2xs uppercase tracking-widest text-muted-foreground">Stress Testing</span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
            Sensitivity Overlay
            <HelpTooltip tip="Stress-test your deal across realistic downside scenarios" articleId="ft-3" className="ml-2 align-middle" />
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            See how fragile or durable this deal is under realistic stress — rent miss, vacancy, CapEx overrun, expense pressure, exit cap expansion, and rate increases.
          </p>
        </motion.div>

        {/* Live overlay */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
          <SensitivityOverlay scoringInput={scoringInput} variant="detail" />
        </motion.div>

        {/* Actions */}
        <div className="flex gap-3 pb-8">
          <Button variant="outline" onClick={() => navigate("/results")} className="gap-1.5 font-display">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Results
          </Button>
          <Button variant="outline" onClick={() => navigate("/assumptions")} className="gap-1.5 font-display">
            Edit Assumptions
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Sensitivity;
